import { useState, useContext, useEffect } from "react";
import styles from "./Onboarding.module.css";
import { AppConfigContext } from "../../context/AppConfigContext";
import { endPoints, getData, postData } from "../../utilities/networkCalls";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { Button, Collapsible, Icon, Link, ProgressBar, Tooltip } from "@shopify/polaris";
import { CheckIcon, XIcon } from "@shopify/polaris-icons";
import { useShowOnboarding } from "../../context/ShowOnboardingContext";
import { useNavigate } from "react-router-dom";

const Onboarding = ({
    currentPlanExists,
    shouldShowTrial,
}: {
    currentPlanExists: boolean;
    shouldShowTrial: boolean;
}) => {
    const {
        setShowOnboarding,
        hasOfferCreated,
        setHasOfferCreated,
        offerWorkingProperly,
        setOfferWorkingProperly,
        isExtensionEnableOnboarding,
        setIsExtensionEnableOnboarding,
    } = useShowOnboarding();

    const goTo = useNavigate();

    const appConfigContext = useContext(AppConfigContext);
    const extensionUrl = `https://admin.shopify.com/store/${appConfigContext.shop}/themes/current/editor?context=apps&activateAppId=${appConfigContext.extensionId}%2Fkefi`;

    const updateOnboardingStatus = async () => {
        try {
            setShowOnboarding("0");
            await postData({
                payload: {
                    status: false,
                },
                url: endPoints.updateOnboardingStatus,
            });
        } catch (error) {
            console._log(error);
        }
    };

    const [checkingManualExtensionStatus, setCheckingManualExtensionStatus] = useState(false);

    const checkExtension = async () => {
        setCheckingManualExtensionStatus(true);
        const res = await getData({ url: endPoints.extensionStatus });
        if (res.status === 200) {
            if (res.data.status === 1) {
                setIsExtensionEnableOnboarding("1");
                updateExtensionOnboarding();
                setExpandStep1(false);
                if (hasOfferCreated == "0") {
                    setExpandStep2(true);
                } else if (offerWorkingProperly == "0") {
                    setExpandStep3(true);
                }
            } else {
                setIsExtensionEnableOnboarding("0");
            }
        } else {
            setIsExtensionEnableOnboarding("0");
        }
        setCheckingManualExtensionStatus(false);
    };

    useEffect(() => {
        const getOfferStatus = async () => {
            try {
                const res = await getData({ url: endPoints.getOnboardingData });
                setHasOfferCreated(res?.data?.bundleCreate ? "1" : "0");
                if (res?.data?.bundleCreate) {
                    setExpandStep2(false);
                }
                if (res?.data?.bundleCreate && offerWorkingProperly == "0") {
                    setExpandStep3(true);
                }
            } catch (error) {
                console._log(error);
            }
        };

        getOfferStatus();
    }, []);

    const updateOfferWorkingStatus = async () => {
        try {
            setOfferWorkingProperly("1");
            setExpandStep3(false);
            setExpandOnboarding(false);
            await postData({
                payload: {
                    offer_working_properly: true,
                },
                url: endPoints.updateOnboardingStatus,
            });
        } catch (error) {
            console._log(error);
        }
    };

    const updateExtensionOnboarding = async () => {
        try {
            await postData({
                payload: {
                    isExtensionEnableOnboarding: true,
                },
                url: endPoints.updateOnboardingStatus,
            });
            setIsExtensionEnableOnboarding("1");
        } catch (error) {
            console._log(error);
        }
    };

    const [expandOnboarding, setExpandOnboarding] = useState(
        currentPlanExists && isExtensionEnableOnboarding == "1" && hasOfferCreated == "1" && offerWorkingProperly == "1"
            ? false
            : true,
    );
    const [expandStep0, setExpandStep0] = useState(currentPlanExists ? false : true);
    const [expandStep1, setExpandStep1] = useState(
        currentPlanExists && isExtensionEnableOnboarding == "0" ? true : false,
    );
    const [expandStep2, setExpandStep2] = useState(
        currentPlanExists && isExtensionEnableOnboarding == "1" && hasOfferCreated == "0" ? true : false,
    );
    const [expandStep3, setExpandStep3] = useState(
        currentPlanExists && isExtensionEnableOnboarding == "1" && hasOfferCreated == "1" && offerWorkingProperly == "0"
            ? true
            : false,
    );

    const [progressCount, setProgressCount] = useState(0);
    useEffect(() => {
        let count = 0;
        if (currentPlanExists) {
            count += 1;
        }
        if (isExtensionEnableOnboarding == "1") {
            count += 1;
        }
        if (hasOfferCreated == "1") {
            count += 1;
        }
        if (offerWorkingProperly == "1") {
            count += 1;
        }
        setProgressCount(count);
    }, [isExtensionEnableOnboarding, hasOfferCreated, offerWorkingProperly]);

    return (
        <div className={styles.mainWrapper}>
            <div>
                <div
                    style={{
                        marginBottom: "2px",
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <h1 className={styles.setupGuide}>Setup Guide</h1>

                    <div
                        style={{
                            display: "flex",
                            gap: "10px",
                            justifyContent: "space-between",
                        }}
                    >
                        <span
                            style={{
                                cursor: "pointer",
                            }}
                            onClick={updateOnboardingStatus}
                        >
                            <Icon source={XIcon} />
                        </span>

                        <span
                            onClick={() =>
                                setExpandOnboarding((ps) => {
                                    return !ps;
                                })
                            }
                            style={{
                                transform: expandOnboarding ? "rotate(90deg)" : "rotate(270deg)",
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <MdOutlineArrowBackIosNew size={14} />
                        </span>
                    </div>
                </div>
                <p className={styles.followContent}>Follow these steps to start using Kefi Bundle Builder</p>

                {progressCount == 4 ? (
                    <p
                        style={{
                            display: "flex",
                            gap: "5px",
                            alignItems: "center",
                            marginTop: "8px",
                        }}
                    >
                        <span>
                            <Icon source={CheckIcon} tone="inherit" />
                        </span>
                        Setup Completed
                    </p>
                ) : (
                    <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ fontSize: "12px", color: "#555" }}>{progressCount} / 4 steps completed</div>
                        <div style={{ width: "14%" }}>
                            <ProgressBar size="small" progress={(progressCount / 4) * 100} tone="primary" />
                        </div>
                    </div>
                )}
            </div>

            <Collapsible
                open={expandOnboarding}
                id=""
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div
                        style={{
                            backgroundColor: expandStep0 ? "#f1f1f1" : "#ffffff",
                        }}
                        className={styles.singleStep}
                    >
                        <div
                            onClick={() => {
                                setExpandStep0((ps) => {
                                    return !ps;
                                });
                                setExpandStep1(false);
                                setExpandStep2(false);
                                setExpandStep3(false);
                            }}
                            className={styles.heading}
                        >
                            {currentPlanExists ? (
                                <div className={styles.tickIcon}>
                                    <Icon source={CheckIcon} tone="inherit" />
                                </div>
                            ) : (
                                <div className={styles.circle}></div>
                            )}
                            {shouldShowTrial ? "Activate your free trial" : "Activate your plan"}
                        </div>

                        <Collapsible
                            open={expandStep0}
                            id=""
                            transition={{
                                duration: "500ms",
                                timingFunction: "ease-in-out",
                            }}
                            expandOnPrint
                        >
                            <div className={styles.stepContentWrapper}>
                                {shouldShowTrial && <p>Choose a plan and start your 14-day free trial.</p>}

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                    }}
                                >
                                    <Button onClick={() => goTo("/billing")} variant="primary">
                                        Choose Plan
                                    </Button>
                                </div>
                            </div>
                        </Collapsible>
                    </div>

                    <div
                        style={{
                            backgroundColor: expandStep1 ? "#f1f1f1" : "#ffffff",
                        }}
                        className={styles.singleStep}
                    >
                        <div
                            onClick={() => {
                                setExpandStep1((ps) => {
                                    return !ps;
                                });
                                setExpandStep0(false);
                                setExpandStep2(false);
                                setExpandStep3(false);
                            }}
                            className={styles.heading}
                        >
                            {isExtensionEnableOnboarding == "1" ? (
                                <div className={styles.tickIcon}>
                                    <Icon source={CheckIcon} tone="inherit" />
                                </div>
                            ) : (
                                <div className={styles.circle}></div>
                            )}
                            Enable Kefi Core Snippet in Shopify
                        </div>

                        <Collapsible
                            open={expandStep1}
                            id=""
                            transition={{
                                duration: "500ms",
                                timingFunction: "ease-in-out",
                            }}
                            expandOnPrint
                        >
                            <div className={styles.stepContentWrapper}>
                                <p>Activate and save the app embed in your theme settings to make your offers live.</p>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                    }}
                                >
                                    <Link url={extensionUrl} target="_blank">
                                        <Button variant="primary">Open Theme Settings</Button>
                                    </Link>

                                    <Button
                                        variant="secondary"
                                        onClick={checkExtension}
                                        loading={checkingManualExtensionStatus}
                                    >
                                        I've Done It
                                    </Button>
                                </div>
                            </div>
                        </Collapsible>
                    </div>

                    <div
                        style={{
                            backgroundColor: expandStep2 ? "#f1f1f1" : "#ffffff",
                        }}
                        className={styles.singleStep}
                    >
                        <div
                            onClick={() => {
                                setExpandStep2((ps) => {
                                    return !ps;
                                });
                                setExpandStep0(false);
                                setExpandStep1(false);
                                setExpandStep3(false);
                            }}
                            className={styles.heading}
                        >
                            {hasOfferCreated == "0" ? (
                                <Tooltip content={"This step will be marked as completed automatically."}>
                                    <div className={styles.circle}></div>
                                </Tooltip>
                            ) : (
                                <div className={styles.tickIcon}>
                                    <Icon source={CheckIcon} tone="inherit" />
                                </div>
                            )}
                            Create your first offer
                        </div>

                        <Collapsible
                            open={expandStep2}
                            id=""
                            transition={{
                                duration: "500ms",
                                timingFunction: "ease-in-out",
                            }}
                            expandOnPrint
                        >
                            <div className={styles.stepContentWrapper}>
                                <p>Choose the offer template you want to create</p>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                    }}
                                >
                                    <Button
                                        onClick={() => {
                                            const target = document.getElementById("catalogueStarting");
                                            if (target) {
                                                const offset = -10;
                                                const y =
                                                    target.getBoundingClientRect().top + window.pageYOffset + offset;
                                                window.scrollTo({ top: y, behavior: "smooth" });
                                            }
                                        }}
                                        variant="primary"
                                    >
                                        Create Offer
                                    </Button>
                                </div>
                            </div>
                        </Collapsible>
                    </div>

                    <div
                        style={{
                            backgroundColor: expandStep3 ? "#f1f1f1" : "#ffffff",
                        }}
                        className={styles.singleStep}
                    >
                        <div
                            onClick={() => {
                                setExpandStep3((ps) => {
                                    return !ps;
                                });
                                setExpandStep0(false);
                                setExpandStep1(false);
                                setExpandStep2(false);
                            }}
                            className={styles.heading}
                        >
                            {offerWorkingProperly == "0" ? (
                                <div className={styles.circle}></div>
                            ) : (
                                <div className={styles.tickIcon}>
                                    <Icon source={CheckIcon} tone="inherit" />
                                </div>
                            )}
                            Confirm your offer is working properly
                        </div>

                        <Collapsible
                            open={expandStep3}
                            id=""
                            transition={{
                                duration: "500ms",
                                timingFunction: "ease-in-out",
                            }}
                            expandOnPrint
                        >
                            <div className={styles.stepContentWrapper}>
                                <p>
                                    Test that your offer displays correctly on the store and that all discounts apply in
                                    the cart and at checkout.
                                </p>

                                {hasOfferCreated == "0" ? (
                                    <>
                                        <div
                                            style={{
                                                display: "flex",
                                                gap: "10px",
                                            }}
                                        >
                                            <Tooltip content="Activate an offer first">
                                                <Button onClick={updateOfferWorkingStatus} variant="primary" disabled>
                                                    Yes, I Confirm
                                                </Button>
                                            </Tooltip>
                                            <Tooltip content="Activate an offer first">
                                                <Button
                                                    onClick={() => {
                                                        // window.userengage("widget.open");
                                                        window.kefi_chat.open();
                                                        // updateOfferWorkingStatus();
                                                    }}
                                                    variant="secondary"
                                                    disabled
                                                >
                                                    Contact Support
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <Button onClick={updateOfferWorkingStatus} variant="primary">
                                            Yes, I Confirm
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                // window.userengage("widget.open");
                                                window.kefi_chat.open();
                                                // updateOfferWorkingStatus();
                                            }}
                                            variant="secondary"
                                        >
                                            Contact Support
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Collapsible>
                    </div>
                </div>
            </Collapsible>
        </div>
    );
};

export default Onboarding;
